import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteTask, usePatchTask, useTasks, useTaskTotal, type TaskTypeFilter } from "../../api/tasks";
import { StatusBadge } from "../../components/StatusBadge";
import { CursorPager } from "../../components/CursorPager";
import { TaskDeviceProgressTable } from "../../components/TaskDeviceProgressTable";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import type { Task } from "../../api/types";
import { extractErrorMessage } from "../../api/errors";
import { ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const statusOptions = [
  { value: "scheduled", label: "Scheduled" },
  { value: "queued", label: "Queued" },
  { value: "in_progress", label: "In progress" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "completed_with_errors", label: "With errors" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

function TaskCard({
  task,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  task: Task;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const failedCount = task.failedDevices;
  const completedCount = task.completedDevices;
  const remaining = task.totalDevices - completedCount - failedCount;

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex w-full items-center justify-between gap-3 px-5 py-3.5 hover:bg-gray-50/50 dark:hover:bg-riot-navy-700/40">
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900 dark:text-white">{task.name}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-4 text-xs text-gray-400 dark:text-riot-navy-400">
            <span>ID: {task.taskId.slice(0, 8)}</span>
            <span>Created at: {new Date(task.createdAt).toLocaleString()}</span>
            <span>Created by: {task.createdBy}</span>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {completedCount > 0 && <StatusBadge status="completed" count={completedCount} />}
          {failedCount > 0 && <StatusBadge status="failed" count={failedCount} />}
          {remaining > 0 && <StatusBadge status={task.status} count={remaining} />}
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-riot-navy-700 dark:hover:text-riot-gold-400"
            aria-label={`Edit ${task.name}`}
            title="Edit task"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-300"
            aria-label={`Delete ${task.name}`}
            title="Delete task"
          >
            <Trash2 size={14} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-gray-400 dark:text-riot-navy-400" /> : <ChevronDown size={16} className="text-gray-400 dark:text-riot-navy-400" />}
        </div>
      </div>
    </div>
  );
}

function TaskDevicePanel({ task }: { task: Task }) {
  return (
    <div className="border-t border-gray-200 bg-gray-50/50 px-5 py-4">
      <TaskDeviceProgressTable taskId={task.taskId} totalDevices={task.totalDevices} />
    </div>
  );
}

export function TaskListPage() {
  const navigate = useNavigate();
  const patchTask = usePatchTask();
  const deleteTask = useDeleteTask();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<TaskTypeFilter | "">("");
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [editTarget, setEditTarget] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [formError, setFormError] = useState("");

  // Paging restarts whenever a filter or the page size changes.
  const paging = useCursorPaging(JSON.stringify([statusFilter, typeFilter, pageSize]));
  const { data, isLoading, isPlaceholderData } = useTasks({
    status: statusFilter || undefined,
    taskType: typeFilter || undefined,
    cursor: paging.cursor,
    limit: pageSize,
  });
  const totalQuery = useTaskTotal({ status: statusFilter || undefined, taskType: typeFilter || undefined });
  const tasks = data?.data ?? [];
  const hasMore = !!data?.pagination.hasMore;
  // The server's count for the filter — never the length of this page.
  const total = totalQuery.isPlaceholderData ? undefined : totalQuery.data?.total;
  const totalIsLowerBound = !totalQuery.isPlaceholderData && !!totalQuery.data?.isLowerBound;

  // An expanded task belongs to the page it was opened on.
  function showPage(move: () => void) {
    setExpandedTask(null);
    move();
  }

  function nextPage() {
    const cursor = data?.pagination.cursor;
    if (!cursor || isPlaceholderData) return;
    showPage(() => paging.next(cursor, tasks.length));
  }

  function startEdit(task: Task) {
    setEditTarget(task);
    setEditName(task.name);
    setEditDescription(task.description ?? "");
    setFormError("");
  }

  async function saveEdit() {
    if (!editTarget) return;

    setFormError("");
    try {
      await patchTask.mutateAsync({
        id: editTarget.taskId,
        name: editName.trim(),
        description: editDescription,
      });
      setEditTarget(null);
    } catch (err) {
      setFormError(extractErrorMessage(err, "Failed to update task"));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;

    setFormError("");
    try {
      await deleteTask.mutateAsync(deleteTarget.taskId);
      if (expandedTask === deleteTarget.taskId) setExpandedTask(null);
      setDeleteTarget(null);
    } catch (err) {
      setFormError(extractErrorMessage(err, "Failed to delete task"));
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Tasks
          {total != null && (
            <span className="ml-1.5 text-sm font-normal text-gray-400 dark:text-riot-navy-400">
              ({total.toLocaleString()}{totalIsLowerBound ? "+" : ""})
            </span>
          )}
        </h2>
        <button
          onClick={() => navigate("/tasks/create")}
          className="rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
        >
          Create task
        </button>
      </div>

      {/* Filters */}
      <div className="mb-3 flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => { setExpandedTask(null); setStatusFilter(e.target.value); }}
          className="rounded-md border border-gray-300 bg-riot-navy-50 px-2.5 py-1.5 text-xs text-gray-700 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200"
          aria-label="Filter by status"
        >
          <option value="">Status</option>
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => { setExpandedTask(null); setTypeFilter(e.target.value as TaskTypeFilter | ""); }}
          className="rounded-md border border-gray-300 bg-riot-navy-50 px-2.5 py-1.5 text-xs text-gray-700 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200"
          aria-label="Filter by task type"
        >
          <option value="">Task type</option>
          <option value="firmware_update">Firmware</option>
          <option value="resource_update">Resource files</option>
        </select>
        <div className="flex-1" />
        <CursorPager
          page={paging.page}
          totalPages={totalPagesFor(paging.page, pageSize, hasMore, total)}
          hasMore={hasMore}
          rangeLabel={formatRange(paging.offset, tasks.length, total, totalIsLowerBound)}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={(size) => {
            setExpandedTask(null);
            setPageSize(size);
          }}
          onNext={nextPage}
          onPrev={() => showPage(paging.prev)}
          onFirst={() => showPage(paging.first)}
        />
      </div>

      {/* Task cards */}
      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading tasks...</div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800">
          {tasks.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-gray-400 dark:text-riot-navy-400">No tasks found</p>
          )}
          {tasks.map((task) => (
            <div key={task.taskId}>
              <TaskCard
                task={task}
                expanded={expandedTask === task.taskId}
                onToggle={() =>
                  setExpandedTask(expandedTask === task.taskId ? null : task.taskId)
                }
                onEdit={() => startEdit(task)}
                onDelete={() => {
                  setDeleteTarget(task);
                  setFormError("");
                }}
              />
              {expandedTask === task.taskId && (
                <TaskDevicePanel task={task} />
              )}
            </div>
          ))}
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Edit task">
          <div className="w-full max-w-lg rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Edit task</h3>
            <div className="mt-4 space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-riot-navy-100">
                Task name
                <input
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700 dark:text-riot-navy-100">
                Description
                <textarea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                />
              </label>
            </div>

            {formError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{formError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={patchTask.isPending || !editName.trim()}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {patchTask.isPending ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Delete task confirmation">
          <div className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete task?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">
              {deleteTarget.name} will be cancelled if it is still active, then removed from task lists.
            </p>

            {formError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{formError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteTask.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {deleteTask.isPending ? "Deleting..." : "Delete task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
